import {
  useTagPollSettingsQuery,
  useUpdateTagPollSettingsMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Card, CardContent } from '../../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { formatCadence } from '../connection-health/cadence'
import { buildPollIntervalOptions, DEFAULT_POLL_INTERVAL_MS } from './poll-interval'

/**
 * Org-wide cadence for the unified tag poller (`/ontology/tags:pollSettings`).
 * Only poll-acquired tags are affected; subscriptions and streams push on
 * change. The proxy applies a change on its next reconcile.
 */
export function TagPollingIntervalCard() {
  const { data: settings, isLoading, error } = useTagPollSettingsQuery()
  const [updateSettings, { isLoading: isSaving, error: saveError }] =
    useUpdateTagPollSettingsMutation()

  const heading = <h3 className="text-sm font-semibold text-ink">Tag polling interval</h3>

  if (isLoading && !settings) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          {heading}
          <p className="text-xs text-ink-3">Loading tag polling settings…</p>
        </CardContent>
      </Card>
    )
  }

  if (error || !settings) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          {heading}
          <p className="text-xs text-ink-3">
            Tag polling settings are unavailable. Check the proxy connection and reload.
          </p>
        </CardContent>
      </Card>
    )
  }

  const currentMs = settings.pollFrequencyMs || DEFAULT_POLL_INTERVAL_MS
  const options = buildPollIntervalOptions(currentMs)

  const onSelect = (raw: string) => {
    const next = Number(raw)
    if (!Number.isFinite(next) || next <= 0 || next === currentMs) return
    updateSettings({ pollFrequencyMs: next }).catch(() => undefined)
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          {heading}
          <p className="text-xs text-ink-3">
            How often polled tags (Ignition WebDev, OPC relay, MTConnect fallback, and database data
            points) are read. OPC UA subscriptions and MTConnect streams push changes as they happen
            and are unaffected. Condition triggers still evaluate every 5 seconds, and database data
            points are never queried faster than the database cadence floor.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-ink-2">
            Currently every <span className="font-medium text-ink">{formatCadence(currentMs)}</span>
          </span>
          <div className="w-40">
            <Select value={String(currentMs)} onValueChange={onSelect} disabled={isSaving}>
              <SelectTrigger aria-label="Tag polling interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {saveError ? (
          <p className="text-xs text-st-down" role="alert">
            Could not update the polling interval: {saveError}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
